import React, { useState, useEffect, useRef } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import classes from "./styles.module.scss";
import { actionNotifier } from "../../components/ui/toast";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "600px",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    maxHeight: "90vh",
    overflowY: "auto",
    outline: "none"
};

const defaultNotification = {
    title: "",
    description: "",
    image: null,
    alt: "",
    type: "image",
    order: 1,
    is_active: true,
    start_date: "",
    end_date: "",
};

const PopupFormModal = ({ open, handleClose, handleSave, initialData, getImageLink, isLoading }) => {
    const [notification, setNotification] = useState(defaultNotification);
    const fileInputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        if (initialData) {
            setNotification({
                ...initialData,
                start_date: initialData.start_date ? new Date(initialData.start_date).toISOString().slice(0, 16) : "",
                end_date: initialData.end_date ? new Date(initialData.end_date).toISOString().slice(0, 16) : "",
            });
        } else {
            setNotification(defaultNotification);
        }
    }, [initialData, open]);

    const handleChange = (field, value) => {
        setNotification(prev => ({ ...prev, [field]: value }));
    };

    const handleFileSelect = (file) => {
        if (file) {
            if (!file.type.startsWith('image/')) {
                actionNotifier.error("Please select a valid image file");
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                actionNotifier.error("File size must be less than 5MB");
                return;
            }
            handleChange("image", file);
        }
    };

    const removeImage = () => {
        handleChange("image", null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const onSubmit = async (e) => {
        e.preventDefault();

        if (!notification.title.trim() || !notification.description.trim() || !notification.image || !notification.start_date || !notification.end_date) {
            actionNotifier.error("Please fill all required fields");
            return;
        }
        if (new Date(notification.end_date) <= new Date(notification.start_date)) {
            actionNotifier.error("End date must be after start date");
            return;
        }

        try {
            setIsUploading(true);
            let imageUrl = notification.image;

            if (notification.image instanceof File) {
                const formData = new FormData();
                formData.append('image', notification.image);
                const response = await getImageLink(formData).unwrap();
                const link = response?.link || response?.data?.link;
                if (link) {
                    imageUrl = link;
                } else {
                    throw new Error("Failed to get image link");
                }
            }

            const finalData = {
                ...notification,
                image: imageUrl,
                start_date: new Date(notification.start_date).toISOString(),
                end_date: new Date(notification.end_date).toISOString()
            };

            await handleSave(finalData);
            setIsUploading(false);
            handleClose();
        } catch (error) {
            console.error(error);
            setIsUploading(false);
            actionNotifier.error(error?.data?.message || error.message || "Failed to process notification");
        }
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                <div className={classes.modal_header}>
                    <h2 className={classes.modal_title}>{initialData ? "Edit Notification" : "Add Notification"}</h2>
                    <button onClick={handleClose} className={classes.close_modal_btn}><CloseIcon /></button>
                </div>
                <form onSubmit={onSubmit} className={classes.modal_form}>
                    <div className={classes.form_grid}>
                        <div className={classes.form_group}>
                            <label>Title *</label>
                            <input
                                type="text"
                                placeholder="Enter notification title"
                                value={notification.title}
                                onChange={(e) => handleChange("title", e.target.value)}
                                required
                            />
                        </div>

                        <div className={classes.form_group}>
                            <label>Alt Text</label>
                            <input
                                type="text"
                                placeholder="Image alt text"
                                value={notification.alt}
                                onChange={(e) => handleChange("alt", e.target.value)}
                            />
                        </div>

                        <div className={`${classes.form_group} ${classes.form_group_full}`}>
                            <label>Description *</label>
                            <textarea
                                placeholder="Enter notification description"
                                value={notification.description}
                                onChange={(e) => handleChange("description", e.target.value)}
                                required
                            />
                        </div>

                        <div className={`${classes.form_group} ${classes.form_group_full}`}>
                            <label>Image *</label>
                            {!notification.image ? (
                                <div
                                    className={classes.image_upload_area}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <CloudUploadIcon className={classes.upload_icon} />
                                    <p>Click to upload image (Max 5MB)</p>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={fileInputRef}
                                        onChange={(e) => handleFileSelect(e.target.files[0])}
                                    />
                                </div>
                            ) : (
                                <div className={classes.image_preview}>
                                    <img
                                        src={notification.image instanceof File ? URL.createObjectURL(notification.image) : notification.image}
                                        alt={notification.alt || "preview"}
                                    />
                                    <span className={classes.file_name}>
                                        {notification.image instanceof File ? notification.image.name : "Uploaded Image"}
                                    </span>
                                    <button
                                        type="button"
                                        className={classes.remove_image_btn}
                                        onClick={removeImage}
                                    >
                                        <CloseIcon />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className={classes.form_group}>
                            <label>Type</label>
                            <select
                                value={notification.type}
                                onChange={(e) => handleChange("type", e.target.value)}
                            >
                                <option value="image">Image</option>
                                <option value="video">Video</option>
                                <option value="text">Text</option>
                            </select>
                        </div>

                        <div className={classes.form_group}>
                            <label>Order</label>
                            <input
                                type="number"
                                min="1"
                                value={notification.order}
                                onChange={(e) => handleChange("order", parseInt(e.target.value) || 1)}
                            />
                        </div>

                        <div className={classes.form_group}>
                            <label>Start Date *</label>
                            <input
                                type="datetime-local"
                                value={notification.start_date}
                                onChange={(e) => handleChange("start_date", e.target.value)}
                                required
                            />
                        </div>

                        <div className={classes.form_group}>
                            <label>End Date *</label>
                            <input
                                type="datetime-local"
                                value={notification.end_date}
                                onChange={(e) => handleChange("end_date", e.target.value)}
                                required
                            />
                        </div>

                        <div className={classes.form_group}>
                            <div className={classes.checkbox_group}>
                                <input
                                    type="checkbox"
                                    id="is_active_modal"
                                    checked={notification.is_active}
                                    onChange={(e) => handleChange("is_active", e.target.checked)}
                                />
                                <label htmlFor="is_active_modal">Active</label>
                            </div>
                        </div>
                    </div>

                    <div className={classes.button_container}>
                        <button type="button" onClick={handleClose} className={classes.cancel_btn}>Cancel</button>
                        <button type="submit" className={classes.submit_btn} disabled={isLoading || isUploading}>
                            {isLoading || isUploading ? "Saving..." : "Save"}
                        </button>
                    </div>
                </form>
            </Box>
        </Modal>
    );
};

export default PopupFormModal;
